import * as Yup from "yup";
import { RESERVED_WORDS } from "../../constants";

export const PROFILE_VALIDATION_SCHEMA = Yup.object().shape({
  // Personal Information
  first_name: Yup.string()
    .matches(
      /^[A-Za-z\s'-]+$/,
      "Name can only contain letters, spaces, apostrophes, and hyphens"
    )
    .min(2, "Name must be between 2 and 50 characters")
    .max(50, "Name must be between 2 and 50 characters")
    .required("First Name is required"),
  last_name: Yup.string()
    .matches(
      /^[A-Za-z\s'-]+$/,
      "Name can only contain letters, spaces, apostrophes, and hyphens"
    )
    .min(2, "Name must be between 2 and 50 characters")
    .max(50, "Name must be between 2 and 50 characters")
    .required("Last Name is required"),
  username: Yup.string()
    .matches(
      /^[A-Za-z0-9-_.]+$/,
      "Username can only contain letters, numbers, hyphens, underscores, and periods."
    )
    // .min(3, 'Username must be between 3 and 16 characters.')
    // .max(16, 'Username must be between 3 and 16 characters.')
    .notOneOf(
      RESERVED_WORDS,
      "This username contains a reserved word and cannot be used"
    )
    .test(
      "different-from-given-name",
      "Username and Display Name cannot be the same.",
      function (value) {
        const { display_name } = this.parent; // Access the other field in the schema
        return value !== display_name; // Ensure username is not the same as display_name
      }
    )
    .required("Username is required"),
  display_name: Yup.string()
    .matches(
      /^[A-Za-z0-9._-]+$/,
      `Cannot have spaces, only letters, numbers, hyphens (-), underscores (_), or periods (.)`
    )
    .min(3, "Display name must be between 3 and 20 characters long.")
    .max(20, "Display name must be between 3 and 20 characters long.")
    .test(
      "no-personal-info-substring",
      "Display name cannot contain your first name, last name, or username",
      function (value) {
        const { first_name, last_name, username } = this.parent;
        if (!value) return true; // Skip validation if display name is empty

        const lowerValue = value.toLowerCase();
        return (
          (!first_name || !lowerValue.includes(first_name.toLowerCase())) &&
          (!last_name || !lowerValue.includes(last_name.toLowerCase())) &&
          (!username || !lowerValue.includes(username.toLowerCase()))
        );
      }
    )
    .required("Display Name is required"),

  native_language: Yup.string()
    .required("Native Language is required")
    .test(
      "no-duplicate-languages",
      "You cannot enter the same language twice",
      function (value) {
        const { native_language, other_language } = this.parent;
        return !(
          native_language &&
          other_language &&
          native_language === other_language
        );
      }
    ),
  other_language: Yup.string().notRequired().nullable(),
  phone: Yup.string()
    // .test('is-valid-phone', 'Please enter a valid phone number', (value) =>
    //     isPossiblePhoneNumber(value || '')
    // )
    .test(
      "no-leading-zero-or-one",
      "Phone number cannot start with 0 or 1",
      (value) => {
        return value ? !/^[01]/.test(value) : true;
      }
    )
    .required("Phone number is required"), // Restrict length

  other_phone: Yup.string()
    .test(
      "no-leading-zero-or-one",
      "Phone number cannot start with 0 or 1",
      (value) => {
        return value ? !/^[01]/.test(value) : true;
      }
    )
    .nullable()
    .notRequired(),
  email: Yup.string()
    .email("Invalid email format.")
    .min(6, "Email must be at least 6 characters long.")
    .max(254, "Email must be less than 254 characters long.")

    .required("Email is required"),
  additional_email: Yup.string()
    .notRequired()
    .nullable()
    .notOneOf(
      [Yup.ref("email")],
      "Additional email cannot be the same as the primary email"
    ),
  bio: Yup.string()
    .min(10, "Bio must be at least 10 characters long.")
    .max(255, "Bio must be less than 255 characters long.")

    .required("Bio is required"),

  // Documents
  air_men: Yup.mixed().required("Airmen Certificate Front is required"),
  air_men_back: Yup.mixed().required("Airmen Certificate Back is required"),
  driving_license: Yup.mixed().required("Driving License Front is required"),
  driving_license_back: Yup.mixed().required(
    "Driving License Back is required"
  ),

  // Address Information
  address: Yup.string().required("Street Address is required"),
  unit_no: Yup.string().notRequired(),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zip_code: Yup.string().required("ZIP/Postal Code is required"),
  neighbourhood: Yup.string().notRequired(),
  country: Yup.string().required("Country is required"),
  same_as_mailing: Yup.boolean(),

  // Emergency Contact
  contact_name: Yup.string().required("Contact Name is required"),

  contact_relationship: Yup.string().required("Relationship is required"),
  contact_email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  contact_phone: Yup.string()
    // .test('is-valid-phone', 'Please enter a valid phone number', (value) =>
    //     isPossiblePhoneNumber(value || '')
    // )
    .required("Contact Phone number is required"),

  image: Yup.mixed()
    // .test('fileSize', 'File size is too large (max 3.1 MB)', (value) => {
    //     // Skip validation if value is a string (URL)
    //     if (typeof value === 'string') return true;
    //     // If value is a File object, check size
    //     if (value && value.size) return value.size <= 3.1 * 1024 * 1024;
    //     // If no value, return false (or true if optional)
    //     return false; // or `true` if the field is optional
    // })
    .test("fileType", "Unsupported file format", (value) => {
      // Skip validation if value is a string (URL)
      if (typeof value[0] === "string") return true;
      // If value is a File object, check type
      if (value[0] && value[0].type) {
        return ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
          value[0].type
        );
      }
      // If no value, return false (or true if optional)
      return false; // or `true` if the field is optional
    }),
});
